import { axios_server, handleRequestError, payment_backend } from ".";
import { createHeaderToken } from "../helpers";
import router from "next/router";
import {
  AddDomain,
  DomainPaymentStorageType,
  DomainPurchaseType,
} from "../types";
import posthog from "posthog-js";
import { toast } from "sonner";
import { AxiosError } from "axios";

export const getDomains = async (url: string) => {
  const { data } = await axios_server.get(url);
  return data;
};

export const createDomain = (addDomain: AddDomain) => {
  const { id, name, teamId } = addDomain;

  return new Promise((resolve, reject) => {
    axios_server
      .post(`/v1/domains/${id || ""}`, { name, teamId })
      .then((res) => {
        posthog.capture("Login", addDomain);
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const deleteDomain = ({
  projectId,
  id,
}: {
  projectId?: string;
  id: string;
}) =>
  new Promise((resolve, reject) => {
    axios_server
      .delete(`/v1/domains/${id}/${projectId ? projectId : ""}`)
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });

export const updateDomain = (name: string, id: string) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .patch(`/v1/domains/${id}`, { name })
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });

export const updateNameServers = ({
  id,
  nameservers,
}: {
  id: string;
  nameservers: string[];
}) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .post(`/v1/domains/${id}/nameservers`, { nameservers })
      .then(resolve)
      .catch(reject);
  });

export const createDNS = (
  body: {
    domain: string;
    record: { type: string; value: string; name: string };
  },
  teamId?: string,
) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .post(
        `https://dns.brimble.io/dns${teamId ? `?teamId=${teamId}` : ""}`,
        body,
      )
      .then(resolve)
      .catch(reject);
  });

export const updateDNS = (
  body: {
    domain: string;
    record: { type: string; value: string; name: string };
    recordID: string;
  },
  teamId?: string,
) => {
  posthog.capture("Update DNS", body);
  return new Promise<any>((resolve, reject) => {
    axios_server
      .patch(
        `https://dns.brimble.io/dns/${body.domain}/${body.recordID}${
          teamId ? `?teamId=${teamId}` : ""
        }`,
        {
          record: body.record,
        },
      )
      .then(resolve)
      .catch(reject);
  });
};
export const deleteDNS = (
  body: { domain: string; recordID: string },
  teamId?: string,
) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .delete(
        `https://dns.brimble.io/dns/${body.domain}/${body.recordID}${
          teamId ? `?teamId=${teamId}` : ""
        }`,
      )
      .then(resolve)
      .catch(reject);
  });

export const searchDomains = (domain: string) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .post(`/v1/domains/sale/search`, { name: domain })
      .then(resolve)
      .catch(reject);
  });

export const handlePurchaseDomain = async (
  setIsLoading: (loading: boolean) => void,
  payload: {
    name: string | undefined;
    duration: number;
    cardId: string;
    projectId: null | string;
    privacyEnabled: boolean;
    autoRenewal: boolean;
  },
  cart: DomainPurchaseType | null,
) => {
  posthog.capture("Purchase domain", payload);
  setIsLoading(true);
  const domainPurchasePromise = payment_backend.post(
    `/payment/purchase`,
    payload,
  );
  await toast.promise(domainPurchasePromise, {
    loading: "Purchasing domain...",
    success: () => {
      router.push(
        `/success?type=domain&domainName=${encodeURIComponent(
          cart?.domainName || "",
        )}&redirectToken=${encodeURIComponent(createHeaderToken(payload, 5))}`,
      );
      return "Domain purchased successfully!";
    },
    error: (error) => {
      handleRequestError(error as AxiosError);
      return "Failed to purchase domain";
    },
    finally: () => {
      setIsLoading(false);
    },
  });
};

export const handleTopUpTokens = async (
  setIsLoading: (loading: boolean) => void,
  amount: number,
  onSuccess?: () => void,
) => {
  setIsLoading(true);
  const payload = {
    type: "LLM_TOKENS",
    amount,
    metadata: {
      description: `LLM Tokens - Top up ${amount} dollars`,
    },
  };
  const tokenPurchasePromise = payment_backend.post(
    `/payment/purchase`,
    payload,
  );
  toast.promise(tokenPurchasePromise, {
    loading: "Purchasing tokens...",
    success: () => {
      onSuccess?.();
      return "Tokens purchased successfully, Changes will reflect in your account shortly";
    },
    error: (error) => {
      const message = handleRequestError(error as AxiosError, "return");
      return message;
    },
    finally: () => {
      setIsLoading(false);
    },
  });
};

export const handleRedirectToPayment = (
  paymentStorage: DomainPaymentStorageType & { userId?: string },
) => {
  localStorage.setItem("paymentDetails", JSON.stringify(paymentStorage));
  if (paymentStorage.userId) router.push("/domains/new/payment");
  else router.push("/login?next=/domains/new/payment");
};

export const transferDomain = (domain: string, project: string) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .post(`/v1/domains/${domain}/transfer/${project}`)
      .then(resolve)
      .catch(reject);
  });
